import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Card,
  Grid,
  Stack,
  Typography,
  TextField,
  Button,
  Chip,
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useQuery } from "@tanstack/react-query";
import ReactApexChart from "react-apexcharts";
import Page from "../Page";
import { BaseTable } from "../BaseTable/BaseTable";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector, paginatedSelector } from "src/react-query/select";
import { useIsGrandParent } from "src/hooks/useIsGrandParent";
import { alertSlice } from "src/store/alert/alert.slice";

// Filter fields shown to the super admin.
const FILTER_FIELDS = [
  { key: "user_id", label: "User ID" },
  { key: "domain", label: "Domain" },
  { key: "charger_id", label: "Charger" },
  { key: "connector_id", label: "Connector" },
  { key: "wallet_id", label: "Wallet" },
  { key: "rfid", label: "RFID" },
  { key: "customer_id", label: "Customer" },
  { key: "payment_id", label: "Payment ID" },
  { key: "transaction_id", label: "Transaction ID" },
  { key: "team_id", label: "Team ID" },
  { key: "price_group_id", label: "Price Group ID" },
  { key: "ip", label: "IP Address" },
  { key: "action", label: "Action (contains)" },
  { key: "search", label: "Search (path/email)" },
];

const EMPTY_INPUTS = FILTER_FIELDS.reduce(
  (acc, f) => ({ ...acc, [f.key]: "" }),
  { startDate: "", endDate: "" }
);

// Fixed height for every activity-log row — keeps rows uniform so they don't
// resize while scrolling the table horizontally.
const LOG_ROW_HEIGHT = 52;

const fmt = (value) => (value ? new Date(value).toLocaleString() : "-");

// Pretty-print the captured request body (stored as a JSON string) for the
// details dialog; falls back to the raw string if it isn't valid JSON.
const formatSummary = (summary) => {
  if (!summary) return "(no request body captured)";
  try {
    return JSON.stringify(JSON.parse(summary), null, 2);
  } catch (e) {
    return String(summary);
  }
};

// Build a clean params object (drop empty values); add end-of-day to endDate.
const buildParams = (applied, includeDates = true) => {
  const params = {};
  Object.entries(applied).forEach(([k, v]) => {
    if (k === "startDate" || k === "endDate") return;
    if (v !== undefined && v !== null && `${v}`.trim() !== "") params[k] = v;
  });
  if (includeDates || true) {
    if (applied.startDate) params.startDate = `${applied.startDate} 00:00:00`;
    if (applied.endDate) params.endDate = `${applied.endDate} 23:59:59`;
  }
  return params;
};

const logColumns = [
  {
    field: "created_date",
    headerName: "Time",
    minWidth: 170,
    flex: 1,
    renderCell: ({ value }) => fmt(value),
  },
  { field: "user_email", headerName: "User", minWidth: 180, flex: 1, valueFormatter: ({ value }) => value || "-" },
  { field: "domain", headerName: "Domain", minWidth: 130, valueFormatter: ({ value }) => value || "-" },
  { field: "action", headerName: "Action", minWidth: 150, flex: 1, valueFormatter: ({ value }) => value || "-" },
  { field: "method", headerName: "Method", minWidth: 90 },
  {
    field: "status_code",
    headerName: "Status",
    minWidth: 90,
    renderCell: ({ row }) => (
      <Chip
        size="small"
        label={row.status_code}
        color={row.success ? "success" : "error"}
        variant="outlined"
      />
    ),
  },
  { field: "charger_id", headerName: "Charger", minWidth: 120, valueFormatter: ({ value }) => value || "-" },
  { field: "connector_id", headerName: "Conn.", minWidth: 80, valueFormatter: ({ value }) => value || "-" },
  { field: "transaction_id", headerName: "Txn", minWidth: 100, valueFormatter: ({ value }) => value || "-" },
  { field: "payment_id", headerName: "Payment", minWidth: 120, valueFormatter: ({ value }) => value || "-" },
  { field: "customer_id", headerName: "Customer", minWidth: 100, valueFormatter: ({ value }) => value || "-" },
  { field: "path", headerName: "Path", minWidth: 240, flex: 1.5, valueFormatter: ({ value }) => value || "-" },
  { field: "ip", headerName: "IP", minWidth: 120, valueFormatter: ({ value }) => value || "-" },
];

const SummaryCard = ({ label, value, color }) => (
  <Card sx={{ p: 2.5, height: "100%" }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h4" sx={{ mt: 0.5, color: color || "text.primary" }}>
      {value ?? 0}
    </Typography>
  </Card>
);

// One row of the "Requests per user" table. Clicking it expands an inline
// drill-down listing exactly which APIs (endpoint + method) that user — or, for
// unauthenticated traffic, that API bucket — is calling. The breakdown is
// fetched lazily (only when expanded) and inherits the page's applied filters
// (date range etc.) via baseParams.
const PerUserRow = ({ row, baseParams, enabled, colSpan }) => {
  const [open, setOpen] = useState(false);
  const isAnonymous = row.user_id == null;

  // Narrow the breakdown to this row: by user_id for a real user, or — for an
  // anonymous API bucket — by unauthenticated traffic (user_id IS NULL) plus the
  // action label. Scoping anonymous rows to unauthenticated=1 keeps the drill-down
  // aligned with the rows that fed the bucket (an action alone can also match a
  // real user's traffic).
  const breakdownParams = useMemo(() => {
    const params = { ...baseParams };
    if (isAnonymous) {
      params.unauthenticated = 1;
      if (row.api_name) params.action = row.api_name;
    } else {
      params.user_id = row.user_id;
    }
    return params;
  }, [baseParams, isAnonymous, row.api_name, row.user_id]);

  const { data: breakdown, isFetching } = useQuery({
    queryKey: queryKeys.auditApiBreakdown(breakdownParams),
    queryFn: fetchWithError,
    select: selector,
    enabled: enabled && open,
    keepPreviousData: true,
  });

  const apis = breakdown ?? [];

  return (
    <>
      <TableRow hover sx={{ cursor: "pointer", "& > td": { borderBottom: "unset" } }} onClick={() => setOpen((o) => !o)}>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small">{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
        </TableCell>
        <TableCell>
          {isAnonymous ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" color="warning" label="API" variant="outlined" />
              <span>{row.api_name || "Anonymous"}</span>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <span>{row.user_email || `User #${row.user_id}`}</span>
              <Typography variant="caption" color="text.secondary">#{row.user_id}</Typography>
            </Stack>
          )}
        </TableCell>
        <TableCell align="right">{row.requestCount}</TableCell>
        <TableCell align="right">{row.successCount}</TableCell>
        <TableCell align="right">{row.failureCount}</TableCell>
        <TableCell>{row.lastIp || "-"}</TableCell>
        <TableCell align="right">{row.distinctIps ?? "-"}</TableCell>
        <TableCell>{fmt(row.lastActivity)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={colSpan}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                APIs called{isAnonymous ? ` (${row.api_name || "anonymous"})` : ` by ${row.user_email || `user #${row.user_id}`}`}
              </Typography>
              {isFetching ? (
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Action</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Endpoint</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">Success</TableCell>
                      <TableCell align="right">Failed</TableCell>
                      <TableCell>Last call</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apis.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">No API activity</TableCell>
                      </TableRow>
                    ) : (
                      apis.map((b, i) => (
                        <TableRow key={`${b.route}-${b.method}-${i}`}>
                          <TableCell>{b.action || "-"}</TableCell>
                          <TableCell>
                            <Chip size="small" label={b.method} variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{b.route || "-"}</TableCell>
                          <TableCell align="right">{b.requestCount}</TableCell>
                          <TableCell align="right">{b.successCount}</TableCell>
                          <TableCell align="right">{b.failureCount}</TableCell>
                          <TableCell>{fmt(b.lastActivity)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const AuditLogs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Super admin = user logged in with a Grand Parent (GP) company (from JWT).
  const isGrandParent = useIsGrandParent();

  useEffect(() => {
    if (!isGrandParent) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: "Not Authorized - Grand Parent (super admin) access required",
        })
      );
      navigate("/dashboard");
    }
  }, [isGrandParent, dispatch, navigate]);

  const [inputs, setInputs] = useState(EMPTY_INPUTS);
  const [applied, setApplied] = useState(EMPTY_INPUTS);
  const [page, setPage] = useState(1);
  const [detailRow, setDetailRow] = useState(null);
  const limit = 50;

  // Append a "View" column that opens the full record (incl. the captured
  // request payload / changes and all entity ids) in a dialog.
  const columns = useMemo(
    () => [
      ...logColumns,
      {
        field: "_details",
        headerName: "",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Button size="small" onClick={() => setDetailRow(row)}>
            View
          </Button>
        ),
      },
    ],
    []
  );

  const analyticsParams = useMemo(() => buildParams(applied), [applied]);
  const listParams = useMemo(
    () => ({ ...buildParams(applied), page, limit }),
    [applied, page]
  );

  const { data: analytics } = useQuery({
    queryKey: queryKeys.auditAnalytics(analyticsParams),
    queryFn: fetchWithError,
    select: selector,
    enabled: isGrandParent,
  });

  const { data: logsResp, isFetching } = useQuery({
    queryKey: queryKeys.auditLogs(listParams),
    queryFn: fetchWithError,
    select: paginatedSelector,
    enabled: isGrandParent,
    keepPreviousData: true,
  });

  const summary = analytics?.summary ?? {};
  const timeseries = analytics?.timeseries ?? [];
  const topActions = analytics?.topActions ?? [];
  const perUser = analytics?.requestsPerUser ?? [];
  const logs = logsResp?.data ?? [];
  const pagination = logsResp?.pagination ?? {};

  const applyFilters = () => {
    setPage(1);
    setApplied(inputs);
  };
  const resetFilters = () => {
    setPage(1);
    setInputs(EMPTY_INPUTS);
    setApplied(EMPTY_INPUTS);
  };

  const timeseriesChart = {
    series: [{ name: "Events", data: timeseries.map((t) => Number(t.count)) }],
    options: {
      chart: { type: "area", toolbar: { show: false } },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      xaxis: { categories: timeseries.map((t) => t.day) },
      title: { text: "Activity over time" },
    },
  };

  const actionsChart = {
    series: [{ name: "Events", data: topActions.map((a) => Number(a.count)) }],
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      dataLabels: { enabled: false },
      xaxis: { categories: topActions.map((a) => a.action || "Unknown") },
      title: { text: "Top actions" },
    },
  };

  return (
    <Page title="Audit Logs">
      <Typography variant="h4" sx={{ mb: 2 }}>
        Audit Logs
      </Typography>

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          {FILTER_FIELDS.map((f) => (
            <Grid item xs={12} sm={6} md={3} key={f.key}>
              <TextField
                fullWidth
                size="small"
                label={f.label}
                value={inputs[f.key]}
                onChange={(e) => setInputs({ ...inputs, [f.key]: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              />
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={inputs.startDate}
              onChange={(e) => setInputs({ ...inputs, startDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={inputs.endDate}
              onChange={(e) => setInputs({ ...inputs, endDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={applyFilters}>
                Apply Filters
              </Button>
              <Button variant="outlined" onClick={resetFilters}>
                Reset
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Total Events" value={summary.totalEvents} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Successful" value={summary.successCount} color="success.main" />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Failed" value={summary.failureCount} color="error.main" />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Active Users" value={summary.activeUsers} color="primary.main" />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Card sx={{ p: 2 }}>
            <ReactApexChart
              type="area"
              series={timeseriesChart.series}
              options={timeseriesChart.options}
              height={300}
            />
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ p: 2 }}>
            <ReactApexChart
              type="bar"
              series={actionsChart.series}
              options={actionsChart.options}
              height={300}
            />
          </Card>
        </Grid>
      </Grid>

      {/* Requests per user */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Requests per user</Typography>
          <Typography variant="caption" color="text.secondary">
            Top {perUser.length} rows{" · "}click any row to drill into the exact
            APIs it is calling. Authenticated users are grouped by user id;
            unauthenticated traffic (login attempts, webhooks, public endpoints)
            is broken out per API and labelled with the API name.
          </Typography>
        </Stack>
        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>User / API</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Success</TableCell>
                <TableCell align="right">Failed</TableCell>
                <TableCell>Last IP</TableCell>
                <TableCell align="right">IPs</TableCell>
                <TableCell>Last activity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {perUser.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No activity
                  </TableCell>
                </TableRow>
              ) : (
                perUser.map((u) => (
                  <PerUserRow
                    key={u.user_id ?? `api:${u.api_name ?? "anonymous"}`}
                    row={u}
                    baseParams={analyticsParams}
                    enabled={isGrandParent}
                    colSpan={8}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Log table */}
      <Card sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">
            Activity log{" "}
            {pagination.total !== undefined && (
              <Chip
                size="small"
                label={
                  pagination.total > 0
                    ? `${(page - 1) * limit + 1}–${Math.min(page * limit, pagination.total)} of ${pagination.total}`
                    : "0 records"
                }
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              disabled={!pagination.hasPreviousPage || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Typography variant="body2">
              Page {pagination.page ?? page} / {pagination.totalPages ?? 1}
            </Typography>
            <Button
              size="small"
              disabled={!pagination.hasNextPage || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </Stack>
        {/* Server already paginates (limit=50). Disable the grid's own
            client-side pager so it doesn't show a conflicting "x of 50"
            footer — the header above is the single source of truth. */}
        <BaseTable
          rows={logs}
          columns={columns}
          isLoading={isFetching}
          getRowId={(row) => row.id}
          pagination={false}
          showExportCsvBtn={false}
          height={650}
          rowHeight={LOG_ROW_HEIGHT}
          // BaseTable wraps cell text (white-space: normal), so rows grow to a
          // variable number of lines; during horizontal scroll the DataGrid
          // virtualizes columns and the "tall" cells change, making row heights
          // jump. Force single-line, fixed-height cells (ellipsis on overflow)
          // so every row stays a uniform LOG_ROW_HEIGHT. Caller sx is merged
          // last in BaseTable, so these override its defaults.
          sx={{
            "& .MuiDataGrid-cell": {
              whiteSpace: "nowrap !important",
              overflow: "hidden !important",
              textOverflow: "ellipsis !important",
              minHeight: `${LOG_ROW_HEIGHT}px !important`,
              maxHeight: `${LOG_ROW_HEIGHT}px !important`,
              alignItems: "center !important",
            },
            "& .MuiDataGrid-cellContent": {
              whiteSpace: "nowrap !important",
              overflow: "hidden !important",
              textOverflow: "ellipsis !important",
            },
            "& .MuiDataGrid-row": {
              minHeight: `${LOG_ROW_HEIGHT}px !important`,
              maxHeight: `${LOG_ROW_HEIGHT}px !important`,
            },
          }}
        />
      </Card>

      {/* Row details: full record incl. the captured request payload (changes)
          and every entity id — several of which have no dedicated column. */}
      <Dialog
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Audit entry{detailRow?.id ? ` #${detailRow.id}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  When / Who
                </Typography>
                <Typography variant="body2">
                  {fmt(detailRow.created_date)} ·{" "}
                  {detailRow.user_email ||
                    (detailRow.user_id
                      ? `User #${detailRow.user_id}`
                      : "Unauthenticated")}
                  {detailRow.domain ? ` · ${detailRow.domain}` : ""}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Action
                </Typography>
                <Typography variant="body2">
                  {detailRow.action || "-"} · {detailRow.method} ·{" "}
                  {detailRow.status_code} (
                  {detailRow.success ? "success" : "failure"})
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Path
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {detailRow.path || "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  IP {detailRow.ip || "-"}
                  {detailRow.duration_ms != null
                    ? ` · ${detailRow.duration_ms} ms`
                    : ""}
                </Typography>
              </Box>
              {(() => {
                const ids = [
                  ["Team", detailRow.team_id],
                  ["Price Group", detailRow.price_group_id],
                  ["Charger", detailRow.charger_id],
                  ["Connector", detailRow.connector_id],
                  ["Transaction", detailRow.transaction_id],
                  ["Payment", detailRow.payment_id],
                  ["Customer", detailRow.customer_id],
                  ["RFID", detailRow.rfid],
                  ["Wallet", detailRow.wallet_id],
                ].filter(
                  ([, v]) => v !== undefined && v !== null && `${v}` !== ""
                );
                return ids.length ? (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Entities
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {ids.map(([label, v]) => (
                        <Chip
                          key={label}
                          size="small"
                          variant="outlined"
                          label={`${label}: ${v}`}
                        />
                      ))}
                    </Stack>
                  </Box>
                ) : null;
              })()}
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Request payload
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 300,
                    overflow: "auto",
                  }}
                >
                  {formatSummary(detailRow.request_summary)}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Captured request body (secrets redacted) — what was submitted,
                  not a before/after diff.
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default AuditLogs;
